import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandSeparator,
} from "@/components/ui/command";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Level, StockPointType } from "@/gen/auth/common_pb";
import { authStore } from "@/providers/authStore";
import normalizeEnums from "@/utils/normalize_enums";
import getPharmacyID from "@/utils/pharmacy_code";
import {
  DialogContent,
  DialogOverlay,
  DialogTrigger,
} from "@radix-ui/react-dialog";
import { CaretSortIcon } from "@radix-ui/react-icons";
import { PopoverAnchor } from "@radix-ui/react-popover";
import {
  Link,
  useMatchRoute,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { CommandItem, CommandList } from "cmdk";
import { ChevronLeft, ChevronsUpDown, Command as CommandIcon, HelpCircle, LogOut, Search, Settings, X } from "lucide-react";
import React, { useEffect, useState } from "react";

function getTabName(tab: string) {
    const path = tab.split("/")[0];
    switch (path) {
        case "":
            return "overview"
        case "invoicing":
            return "reports"
        case "purchase-invoice":
            return "inventory"
        case "purchase-order":
            return "inventory"
        default:
            return path
    }
}

enum OS {
  Windows = "Windows",
  Macintosh = "Macintosh",
  Linux = "Linux",
}

const NavLink = ({ to, children, className, isHome }: { to: string, children: React.ReactNode, className?: string, isHome: boolean }) => {
  const navigate = useNavigate();
  
  const handleClick = (e: React.MouseEvent) => {
    if (getPharmacyID() === "") {
      e.preventDefault();
      return;
    }
    if (!isHome) {
      navigate({ to });
    }
  };
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div 
            className={`${className} ${getPharmacyID() === "" ? 'opacity-50 cursor-not-allowed' : (isHome ? 'cursor-not-allowed' : 'cursor-pointer')}`}
            onClick={handleClick}
          >
            {children}
          </div>
        </TooltipTrigger>
        {getPharmacyID() === "" && (
          <TooltipContent>
            <p>Please select a pharmacy first</p>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
};

const Index = () => {
  const matchRoute = useMatchRoute();
  const router = useRouterState();
  const currentPath = router.location.pathname;
  const navigate = useNavigate();
  const [mobileNavMenuOpen, setMobileNavMenuOpen] = useState(false);
  const [os, setOs] = useState(OS.Windows);
  const [showShortcuts, setShowShortcuts] = useState(false);

  const placeholdres = [
    "Search for medicines",
    "Search for invoices",
    "Search for reports",
  ];
  const [placeHolderIdx, setPlaceHolderIdx] = useState(0);
  const [placeholder, setPlaceholder] = useState(placeholdres[placeHolderIdx]);

  useEffect(() => {
    const ua = window.navigator.userAgent;
    if (ua.includes("Macintosh")) {
      setOs(OS.Macintosh);
    }
    const placeholderRotatior = setInterval(() => {
      //weird issues with useState closures and use effect
      //https://stackoverflow.com/questions/53024496/state-not-updating-when-using-react-state-hook-within-setinterval
      setPlaceHolderIdx((oldIdx) => (oldIdx + 1) % placeholdres.length);
    }, 2500);
    return () => {
      clearInterval(placeholderRotatior);
    };
  }, []);

  let navigationItems = [
    { name: "Overview", path: "/overview" },
    { name: "Sales", path: "/sales" },
    { name: "Inventory", path: "/inventory" },
    { name: "Reports", path: "/invoicing" },
    { name: "Suppliers", path: "/suppliers" },
    { name: "Patients", path: "/patients" },
    { name: "Accounts", path: "/accounts" },
    { name: "Insurance", path: "/insurance", disabled: true },
  ];

  if (localStorage.getItem("level") === Level.STOCK_POINT.toString()) {
    if (localStorage.getItem("stockPointType") === StockPointType.RECEIVING.toString()) {
      navigationItems = [
        { name: "Inventory", path: "/inventory" },
        { name: "Reports", path: "/invoicing" },
        { name: "Suppliers", path: "/suppliers" },
        { name: "Accounts", path: "/accounts" },
      ]
    } else if (localStorage.getItem("stockPointType") === StockPointType.SELLING.toString()) {
      navigationItems = [
        { name: "Overview", path: "/overview" },
        { name: "Sales", path: "/sales" },
        { name: "Inventory", path: "/inventory" },
        { name: "Reports", path: "/invoicing" },
        { name: "Patients", path: "/patients" },
        { name: "Accounts", path: "/accounts" },
        { name: "Insurance", path: "/insurance", disabled: true },
      ];
    }
  } else if (localStorage.getItem("level") === Level.BRANCH.toString()) {
    navigationItems = [
      { name: "Overview", path: "/overview" },
      { name: "Inventory", path: "/inventory" },
      { name: "Reports", path: "/invoicing" },
      { name: "Suppliers", path: "/suppliers" },
      { name: "Patients", path: "/patients" },
      { name: "Accounts", path: "/accounts" },
      { name: "Insurance", path: "/insurance", disabled: true },
    ];
  }

  const stockPointName = localStorage.getItem("stockPointName");
  const companyName = localStorage.getItem("companyName");

  const isHome = currentPath === "/home";

  // Set currentTab to undefined if isHome, otherwise determine the tab
  const currentTab = isHome
    ? undefined
    : navigationItems.find(item => currentPath.startsWith(item.path))?.name.toLowerCase();

  return (
    <nav className="sticky top-0 flex border-b-[1px] bg-background p-4 z-50 gap-4 justify-between mb-4 print:hidden">
      <div className="flex items-center justify-center space-x-4">
      <button
        onClick={() => {
          window.location.href = '/select-access'
        }}
        className="flex items-center hover:opacity-80 transition-opacity"
        aria-label="Switch pharmacy context"
      >
        
        <div className="border rounded-md p-2 flex gap-2 place-items-center">
          {/* <img src="/logo.svg" alt="logo" width={24} height={24} /> */}
          <svg
            width="18"
            height="18"
            viewBox="0 0 82 82"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15.6852 36.5542L21.046 0H27.0024L21.6416 36.5542H15.6852ZM28.7893 36.5542L34.1501 0H40.1065L34.7458 36.5542H28.7893ZM52.0194 7.70602L53.2107 0H59.1671L57.9758 7.70602H52.0194ZM65.1235 7.70602L66.3148 0H72.2712L71.0799 7.70602H65.1235ZM5.95642 18.5735V12.6458H13.8983L13.1041 18.5735H5.95642ZM42.4891 18.5735L43.2833 12.6458H82V18.5735H42.4891ZM5.95642 31.6145V25.6867H12.1114L11.1186 31.6145H5.95642ZM40.5036 31.6145L41.4964 25.6867H82V31.6145H40.5036ZM54.2034 82L60.954 36.5542H66.9104L60.1598 82H54.2034ZM41.0993 82L47.8499 36.5542H53.8063L47.0557 82H41.0993ZM0 50.188V44.2602H41.4964L40.5036 50.188H0ZM69.8886 50.188L70.8814 44.2602H76.0436V50.188H69.8886ZM0 63.2289V57.3012H39.5109L38.7167 63.2289H0ZM67.9031 63.2289L68.8959 57.3012H76.0436V63.2289H67.9031ZM8.93463 82L10.9201 68.1687H16.8765L14.891 82H8.93463ZM22.0387 82L24.0242 68.1687H29.9806L27.9952 82H22.0387Z"
              fill="#212121"
            />
          </svg>
          <div  className="text-sm font-semibold text-muted-foreground gap-2 flex items-center underline underline-offset-2">
            <span> | </span>
            {stockPointName || companyName || "Pharmacy Management System"}

            <ChevronsUpDown className=""></ChevronsUpDown>
          </div>
          {/* <div>
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </div> */}
        </div>
        
        </button>

        <div
          className={`block md:hidden w-[150px]   ${mobileNavMenuOpen ? "rounded-t-md border border-b-white" : "rounded-md border"}`}
        >
          <Popover open={mobileNavMenuOpen} onOpenChange={setMobileNavMenuOpen}>
            <Dialog
              open={mobileNavMenuOpen}
              onOpenChange={setMobileNavMenuOpen}
            >
              <PopoverTrigger asChild>
                <DialogTrigger asChild>
                  <button
                    role="combobox"
                    className="w-full flex flex-row justify-between items-center px-2 py-2 border-none"
                  >
                    {normalizeEnums(getTabName(currentTab || ""))}
                    <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </button>
                </DialogTrigger>
              </PopoverTrigger>
              <PopoverAnchor />
              <DialogOverlay></DialogOverlay>
              <PopoverContent className="border border-t-0 rounded-b-lg w-[150px]">
                <DialogContent asChild>
                  <Command>
                    <CommandList className="w-[150px] border-0">
                      <CommandGroup className="flex flex-col gap-2 px-2 text-normal ">
                        <CommandSeparator />
                        {navigationItems.map((item, index) => (
                          <React.Fragment key={index}>
                            <CommandItem asChild>
                              <NavLink
                                to={item.path}
                                className={`py-2 ${(isHome || item.disabled) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                isHome={isHome}
                              >
                                {item.name}
                              </NavLink>
                            </CommandItem>
                            <CommandSeparator />
                          </React.Fragment>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </DialogContent>
              </PopoverContent>
            </Dialog>
          </Popover>
        </div>
        <div className="hidden md:block h-full">
          <Tabs value={currentTab || ""} className="w-[400px]">
            <TabsList>
              {navigationItems.map((item, index) => (
                <TabsTrigger key={index} value={item.name.toLowerCase()} asChild disabled={isHome || getPharmacyID() === "" || item.disabled}>
                  <Link 
                    to={item.path} 
                    className={`${(isHome || getPharmacyID() === "" || item.disabled) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`} 
                    preload="intent" 
                    disabled={isHome || getPharmacyID() === "" || item.disabled}
                    onClick={e => {
                      if (isHome || getPharmacyID() === "" || item.disabled) {
                        e.preventDefault();
                      }
                    }}
                  >
                    {item.name}
                  </Link>
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </div>
      <div className="flex items-center justify-center space-x-4">
        <div
          role="button"
          onMouseDown={(e) => {
            if (getPharmacyID() === "") {
              e.preventDefault();
              return;
            }
            const commandEvent = new Event("open-command-menu");
            document.dispatchEvent(commandEvent);
          }}
          className={`md:flex hidden border border-input justify-end items-center rounded-md px-2 ${getPharmacyID() === "" ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <div className="flex items-center">
            <Search size={15} className="text-muted-foreground mr-2" />
            <Input
              id="MainSearch"
              placeholder={placeholdres[placeHolderIdx]}
              className="hidden md:block h-8 min-w-lg border-0 text-foreground animate-pulse duration-[5000s] delay-2500 pl-0"
            />
          </div>
          <div className="h-6 w-[1px] bg-border mx-2" />
          <span className="border border-input text-sm rounded-md flex gap-2 items-center px-2 my-2">
            {os === OS.Macintosh ? (
              <span className="flex gap-1 items-center text-xs font-semibold text-muted-foreground">
                <kbd className="text-sm">⌘</kbd> + <kbd>K</kbd>
              </span>
            ) : (
              <span className="flex gap-1 items-center text-xs font-semibold text-muted-foreground">
                <kbd>Ctrl</kbd> + <kbd>K</kbd>
              </span>
            )}
          </span>
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <div className="border h-full rounded-md grid place-items-center cursor-pointer p-2">
              <HelpCircle size={18} />
            </div>
          </PopoverTrigger>
          <PopoverContent className="fixed w-fit p-1.5 max-w-xs right-0 top-0 shadow-lg rounded-md">
            <div className="flex flex-col gap-1">
              <Button
                variant="ghost"
                className="justify-start text-sm h-8"
                onClick={() => setShowShortcuts(true)}
              >
                <CommandIcon className="mr-2 h-4 w-4 text-black" />
                Keyboard Shortcuts
              </Button>
              <hr className="my-2" />
              <Button
                variant="ghost"
                className="justify-start text-sm h-8"
                onClick={() => navigate({ to: "/settings" })}
              >
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </Button>
            </div>
          </PopoverContent>
        </Popover>
        <Sheet open={showShortcuts} onOpenChange={setShowShortcuts}>
          <SheetContent side="right" className="max-w-md w-full overflow-scroll">
            <div className="flex items-center justify-between pb-2">
              <div className="flex items-center gap-1">

              <button
                
                aria-label="Back"
                onClick={() => setShowShortcuts(false)}
                >
                <ChevronLeft className="w-5 h-5 text-gray-500" />
              </button>
              <p className="flex-1 text-center text-lg font-semibold">
                Keyboard Shortcuts
              </p>
                </div>
              <button
                className="p-2"
                aria-label="Close"
                onClick={() => setShowShortcuts(false)}
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <Input
              placeholder="Search shortcuts"
              className="mb-4"
            />
            <div className="space-y-6">
              <div>
                <div className="font-semibold text-sm mb-2">General</div>
                <ul className="space-y-2 text-sm">
                  <ShortcutRow label="Open command menu" keys={os === OS.Macintosh ? ["⌘", "K"] : ["Ctrl", "K"]} />
                  <ShortcutRow label="View keyboard shortcuts" keys={os === OS.Macintosh ? ["⌘", "/"] : ["Ctrl", "/"]} />
                </ul>
              </div>
              <div>
                <div className="font-semibold text-sm mb-2">Sales</div>
                <ul className="space-y-2 text-sm">
                  <ShortcutRow label="Move selection up" keys={["↑"]} />
                  <ShortcutRow label="Move selection down" keys={["↓"]} />
                  <ShortcutRow label="Cycle active sales" keys={["S"]} />
                  <ShortcutRow label="Focus product search" keys={["Space"]} />
                  <ShortcutRow label="Checkout" keys={["Shift", "Enter"]} />
                  <ShortcutRow label="Add new multi-sale" keys={["Shift", "S"]} />
                  <ShortcutRow label="Discard sale" keys={["Shift", "D"]} />
                  <ShortcutRow label="Increase quantity" keys={["+", "→"]} />
                  <ShortcutRow label="Decrease quantity" keys={["-", "←"]} />
                  <ShortcutRow label="Remove selected item" keys={["Shift", "Backspace"]} />
                </ul>
              </div>
            </div>
           
          </SheetContent>
        </Sheet>
        {(
          <div
            className="border h-full rounded-md p-2 grid place-items-center cursor-pointer"
            onClick={() => {
              localStorage.clear();
              authStore.clear();
              navigate({
                to: "/login",
              })
            }}
          >
            <LogOut size={15} />
          </div>
        )}
      </div>
    </nav>
  );
};

export default Index;

// Helper component for shortcut row
function ShortcutRow({ label, keys }: { label: string; keys: string[] }) {
  return (
    <li className="flex items-center justify-between">
      <span>{label}</span>
      <span className="flex gap-1">
        {keys.map((key, idx) => (
          <kbd key={idx} className="px-2 py-1 border rounded bg-muted text-xs font-mono">{key}</kbd>
        ))}
      </span>
    </li>
  );
}